import equal from 'fast-deep-equal'
import { immer } from 'zustand/middleware/immer'

import { Slice } from '../store'
import { UnifiedDipsCriticalInformation } from '../transformers/dipsCriticalInformationTransformer'
import { UnifiedDipsLocalization } from '../transformers/dipsLocalizationTransformer'
import { UnifiedDipsPostponementReason } from '../transformers/dipsPostponementReasonsTransformer'
import { UnifiedDipsResource } from '../transformers/dipsResourceTransformer'
import { UnifiedDipsWard } from '../transformers/dipsWardTransformer'
import { UnifiedScheduledSurgery } from '../transformers/scheduledSurgeryTransformer'
import { UnifiedUnScheduledSurgery } from '../transformers/unScheduledSurgery/unScheduledSurgeryTransformer'

export type EntityTable<K extends string | number, T> = {
    byId: Record<K, T>
    allIds: K[]
} & Record<string, unknown>

function createEntityState<K extends string | number, T>(): EntityTable<K, T> {
    return {
        byId: {} as Record<K, T>,
        allIds: [] as K[],
    }
}

const entityStates = {
    scheduledSurgeries: createEntityState<string, UnifiedScheduledSurgery>(),
    unScheduledSurgeries: createEntityState<string, UnifiedUnScheduledSurgery>(),
    dipsResources: createEntityState<number, UnifiedDipsResource>(),
    dipsWards: createEntityState<number, UnifiedDipsWard>(),
    dipsLocalizations: createEntityState<number, UnifiedDipsLocalization>(),
    dipsPostponementReasons: createEntityState<number, UnifiedDipsPostponementReason>(),
    dipsCriticalInformation: createEntityState<string, UnifiedDipsCriticalInformation>(),
}

export type DipsEntityKey = keyof typeof entityStates

type State = {
    entities: typeof entityStates
}

type Actions = {
    actions: {
        addFullScheduledSurgeries: (entities: UnifiedScheduledSurgery[]) => void
        removeFullScheduledSurgeries: (ids: string[]) => void
        addUnScheduledSurgeries: (entities: UnifiedUnScheduledSurgery[]) => void
        removeUnScheduledSurgeries: (ids: string[]) => void
        addResources: (entities: UnifiedDipsResource[]) => void
        addWards: (entities: UnifiedDipsWard[]) => void
        addLocalizations: (entities: UnifiedDipsLocalization[]) => void
        addPostponementReasons: (entities: UnifiedDipsPostponementReason[]) => void
        addCriticalInformation: (entities: UnifiedDipsCriticalInformation[]) => void
    }
}

export type DipsSlice = {
    dips: State & Actions
}

export const createDipsSlice: Slice<DipsSlice> = immer((set, get) => ({
    dips: {
        entities: entityStates,
        actions: {
            addFullScheduledSurgeries: (entities: UnifiedScheduledSurgery[]) => {
                const changedEntities = entities.filter(entity => {
                    const existing = get().dips.entities.scheduledSurgeries.byId[entity.id]
                    return !existing || !equal(existing, entity)
                })

                if (changedEntities.length === 0) {
                    return
                }

                set(state => {
                    for (const entity of changedEntities) {
                        state.dips.entities.scheduledSurgeries.byId[entity.id] = entity
                    }
                    state.dips.entities.scheduledSurgeries.allIds = Object.keys(state.dips.entities.scheduledSurgeries.byId)
                })
            },
            removeFullScheduledSurgeries: (ids: string[]) => {
                set(state => {
                    for (const id of ids) {
                        delete state.dips.entities.scheduledSurgeries.byId[id]
                    }

                    state.dips.entities.scheduledSurgeries.allIds = Object.keys(state.dips.entities.scheduledSurgeries.byId)
                })
            },
            addUnScheduledSurgeries: (entities: UnifiedUnScheduledSurgery[]) => {
                const changedEntities = entities.filter(entity => {
                    const existing = get().dips.entities.unScheduledSurgeries.byId[entity.bookingId]
                    return !existing || !equal(existing, entity)
                })

                if (changedEntities.length === 0) {
                    return
                }

                set(state => {
                    for (const entity of changedEntities) {
                        state.dips.entities.unScheduledSurgeries.byId[entity.bookingId] = entity
                    }
                    state.dips.entities.unScheduledSurgeries.allIds = Object.keys(state.dips.entities.unScheduledSurgeries.byId)
                })
            },
            removeUnScheduledSurgeries: (ids: string[]) => {
                set(state => {
                    for (const id of ids) {
                        delete state.dips.entities.unScheduledSurgeries.byId[id]
                    }

                    state.dips.entities.unScheduledSurgeries.allIds = Object.keys(state.dips.entities.unScheduledSurgeries.byId)
                })
            },
            addResources: (entities: UnifiedDipsResource[]) => {
                set(state => {
                    for (const entity of entities) {
                        if (entity.id) {
                            state.dips.entities.dipsResources.byId[entity.id] = entity
                        }
                    }

                    state.dips.entities.dipsResources.allIds = Object.keys(state.dips.entities.dipsResources.byId).map(Number)
                })
            },
            addWards: (entities: UnifiedDipsWard[]) => {
                set(state => {
                    for (const entity of entities) {
                        if (entity.id) {
                            state.dips.entities.dipsWards.byId[entity.id] = entity
                        }
                    }
                    state.dips.entities.dipsWards.allIds = Object.keys(state.dips.entities.dipsWards.byId).map(Number)
                })
            },
            addLocalizations: (entities: UnifiedDipsLocalization[]) => {
                set(state => {
                    for (const entity of entities) {
                        if (entity.id) {
                            state.dips.entities.dipsLocalizations.byId[entity.id] = entity
                        }
                    }
                    state.dips.entities.dipsLocalizations.allIds = Object.keys(state.dips.entities.dipsLocalizations.byId).map(Number)
                })
            },
            addPostponementReasons: (entities: UnifiedDipsPostponementReason[]) => {
                set(state => {
                    for (const entity of entities) {
                        if (entity.id) {
                            state.dips.entities.dipsPostponementReasons.byId[entity.id] = entity
                        }
                    }
                    state.dips.entities.dipsPostponementReasons.allIds = Object.keys(state.dips.entities.dipsPostponementReasons.byId).map(Number)
                })
            },
            addCriticalInformation: (entities: UnifiedDipsCriticalInformation[]) => {
                set(state => {
                    for (const entity of entities) {
                        if (entity.patientProxyGuid) {
                            state.dips.entities.dipsCriticalInformation.byId[entity.patientProxyGuid] = entity
                        }
                    }
                    state.dips.entities.dipsCriticalInformation.allIds = Object.keys(state.dips.entities.dipsCriticalInformation.byId)
                })
            },
        },
    },
}))
